/**
 * Cross-tab auth sync (R0 / ROADMAP R1A).
 *
 * Every tab of one browser shares ONE session: the access token + user in
 * localStorage and the httpOnly refresh cookie. A login in another tab
 * therefore switches this tab's account too. This module makes that visible
 * and safe instead of silent:
 *  - other tabs' logins/logouts arrive via `storage` events + BroadcastChannel,
 *    are compared with the user THIS tab last knew, and announced
 *    ("You signed in as X in another tab");
 *  - the current user is reloaded from GET /users/me (deduplicated) on app
 *    start, window focus, after login, after a refreshed token turns out to
 *    belong to someone else, and once after a 403;
 *  - route guards use useVerifiedUser(), which only trusts a user that the
 *    server confirmed for the CURRENT token.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from './api';
import {
  AUTH_CHANGED_EVENT,
  AUTH_CHECK_EVENT,
  APP_TOAST_EVENT,
  dispatchAppEvent,
  readStoredUser,
  tokenUserId,
} from './authEvents';

const CHANNEL_NAME = 'zaynic-auth';
const FOCUS_RECHECK_MS = 15000;
const FORBIDDEN_RECHECK_MS = 10000;
const VERIFIED_FRESH_MS = 60000;

let channel = null;
let started = false;
let tabUser = null; // the user THIS tab last showed (localStorage is shared)
let mePromise = null;
let verified = { token: null, userId: null, at: 0 };
let lastFocusCheck = 0;
let lastForbidden = 0;

const currentToken = () => {
  const raw = localStorage.getItem('access_token');
  return raw && raw !== 'null' && raw !== 'undefined' ? raw : null;
};

export const isSignedIn = () => !!(currentToken() && readStoredUser());

export const displayName = (user) =>
  (user && (user.display_name || user.username || user.email)) || 'another account';

function sameIdentity(a, b) {
  return (
    (a?.id ?? null) === (b?.id ?? null) &&
    !!a?.is_superuser === !!b?.is_superuser &&
    (a?.role ?? null) === (b?.role ?? null) &&
    !!a?.has_active_store === !!b?.has_active_store
  );
}

function announce(kind, user, previous, fromOtherTab) {
  tabUser = user || null;
  dispatchAppEvent(AUTH_CHANGED_EVENT, { kind, user: user || null, previous: previous || null, fromOtherTab });
}

function post(message) {
  try {
    if (channel) channel.postMessage(message);
  } catch (e) {
    /* ignore */
  }
}

/** Compare the shared session with what this tab last knew; announce changes. */
function syncFromStorage(fromOtherTab) {
  const user = currentToken() ? readStoredUser() : null;
  if (!user && !tabUser) return;
  if (!user) {
    verified = { token: null, userId: null, at: 0 };
    announce('logout', null, tabUser, fromOtherTab);
    return;
  }
  if (!sameIdentity(user, tabUser)) {
    announce(tabUser && tabUser.id !== user.id ? 'switched' : 'user', user, tabUser, fromOtherTab);
  }
}

/** Store a fresh session (every login path) and tell the other tabs. */
export function storeSession(accessToken, user) {
  if (accessToken) localStorage.setItem('access_token', accessToken);
  if (user) localStorage.setItem('user_data', JSON.stringify(user));
  if (accessToken && user && tokenUserId(accessToken) === user.id) {
    verified = { token: accessToken, userId: user.id, at: Date.now() };
  }
  announce('login', user, tabUser, false);
  post({ kind: 'login' });
}

/** Clear the session locally and in every other tab. */
export function clearSession() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user_data');
  verified = { token: null, userId: null, at: 0 };
  announce('logout', null, tabUser, false);
  post({ kind: 'logout' });
}

/**
 * GET /users/me for the current token (one request at a time). Updates the
 * stored user and announces a change. Resolves to the user or null.
 */
export function refreshCurrentUser() {
  if (mePromise) return mePromise;
  if (!currentToken()) return Promise.resolve(null);
  mePromise = apiClient
    .get('/users/me')
    .then((res) => {
      const user = res.data;
      const token = currentToken();
      // A login in another tab mid-request: the newer session wins.
      if (!token || tokenUserId(token) !== user?.id) {
        syncFromStorage(true);
        return currentToken() ? readStoredUser() : null;
      }
      localStorage.setItem('user_data', JSON.stringify(user));
      verified = { token, userId: user.id, at: Date.now() };
      syncFromStorage(false);
      return user;
    })
    .catch((err) => {
      if (err?.response?.status === 401) {
        // api.js already cleared the session after a failed refresh.
        syncFromStorage(false);
        return null;
      }
      return currentToken() ? readStoredUser() : null;
    })
    .finally(() => {
      mePromise = null;
    });
  return mePromise;
}

const isVerifiedFresh = () => {
  const token = currentToken();
  return !!token && verified.token === token && Date.now() - verified.at < VERIFIED_FRESH_MS;
};

const isVerifiedForToken = () => {
  const token = currentToken();
  return !!token && verified.token === token;
};

function onForbidden() {
  const now = Date.now();
  if (now - lastForbidden < FORBIDDEN_RECHECK_MS) return;
  lastForbidden = now;
  const before = currentToken() ? readStoredUser() : null;
  refreshCurrentUser().then((user) => {
    // A switched/changed account is explained by the notice + guards.
    if (!user || !before || !sameIdentity(user, before)) return;
    const path = window.location.pathname;
    if ((path === '/admin' || path.startsWith('/admin/')) && !user.is_superuser) {
      dispatchAppEvent(APP_TOAST_EVENT, {
        severity: 'error',
        message: `This needs an admin account. You are signed in as ${displayName(user)}.`,
      });
    }
  });
}

/** Start once per tab (App mount). Safe to call again. */
export function startAuthSync() {
  if (started) return;
  started = true;
  tabUser = currentToken() ? readStoredUser() : null;

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = () => syncFromStorage(true);
    }
  } catch (e) {
    channel = null;
  }

  window.addEventListener('storage', (e) => {
    if (e.key === null || e.key === 'user_data' || e.key === 'access_token') syncFromStorage(true);
  });

  const recheck = () => {
    syncFromStorage(true);
    const now = Date.now();
    if (currentToken() && now - lastFocusCheck > FOCUS_RECHECK_MS) {
      lastFocusCheck = now;
      refreshCurrentUser();
    }
  };
  window.addEventListener('focus', recheck);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') recheck();
  });

  window.addEventListener(AUTH_CHECK_EVENT, (e) => {
    const reason = e?.detail?.reason;
    if (reason === 'forbidden') onForbidden();
    else if (reason === 'session-expired') syncFromStorage(false);
    else refreshCurrentUser();
  });

  if (currentToken()) {
    lastFocusCheck = Date.now();
    refreshCurrentUser();
  }
}

/** Where to go after a successful login (null = stay on this page). */
export function landingPathAfterLogin(user) {
  if (user?.is_superuser) return '/admin';
  const from = window.history.state?.usr?.from;
  if (from?.pathname && !(from.pathname === '/admin' || from.pathname.startsWith('/admin/') || from.pathname === '/admin-login')) {
    return `${from.pathname}${from.search || ''}`;
  }
  const url = new URL(window.location.href);
  if (url.searchParams.has('login')) {
    url.searchParams.delete('login');
    return `${url.pathname}${url.search}`;
  }
  return null;
}

export function goAfterLogin(user) {
  const path = landingPathAfterLogin(user);
  if (path) window.location.assign(path);
  else window.location.reload();
}

/** The stored user, re-rendering on any auth change (no server call). */
export function useAuthUser() {
  const [user, setUser] = useState(() => (isSignedIn() ? readStoredUser() : null));
  useEffect(() => {
    const update = () => setUser(isSignedIn() ? readStoredUser() : null);
    window.addEventListener(AUTH_CHANGED_EVENT, update);
    update();
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, update);
  }, []);
  return user;
}

/**
 * For route guards: the user the server confirmed for the CURRENT token.
 * Re-checks on every navigation and every auth change; blocks rendering
 * (checking=true) whenever the token has not been verified yet.
 */
export function useVerifiedUser() {
  const location = useLocation();
  const [state, setState] = useState(() => ({
    checking: !!currentToken() && !isVerifiedForToken(),
    user: isVerifiedForToken() ? readStoredUser() : null,
  }));

  useEffect(() => {
    let cancelled = false;
    const check = () => {
      if (!currentToken()) {
        setState({ checking: false, user: null });
        return;
      }
      if (isVerifiedForToken()) {
        setState({ checking: false, user: readStoredUser() });
        if (isVerifiedFresh()) return;
      } else {
        setState({ checking: true, user: null });
      }
      refreshCurrentUser().then((user) => {
        if (cancelled) return;
        // Only a user whose id matches the CURRENT token's subject.
        const ok = !!user && tokenUserId(currentToken()) === user.id;
        setState({ checking: false, user: ok ? user : null });
      });
    };
    check();
    window.addEventListener(AUTH_CHANGED_EVENT, check);
    return () => {
      cancelled = true;
      window.removeEventListener(AUTH_CHANGED_EVENT, check);
    };
  }, [location.pathname]);

  return state;
}
